<template>
  <div class="enroll-devices-modal">
    <protection-limit-banner
      v-if="protectionLimit"
      :protection-limit="protectionLimit"
      class="mb-4"
    />

    <div class="subtitle1 mb-1">
      {{ $t("modals.enrollDevices.subtitle") }}
    </div>
    <div class="body2 text-indigo-medium mb-6">
      {{ $t("modals.enrollDevices.description") }}
    </div>

    <div class="d-flex flex-column ga-3">
      <!-- Card 1: Download installer -->
      <div class="enroll-card rounded-lg pa-6 d-flex align-center justify-space-between">
        <div class="d-flex align-start ga-3">
          <v-icon icon="icon-endpoint-xs" size="24" class="mt-1" />
          <div>
            <div class="d-flex align-center ga-2 mb-1">
              <span class="subtitle2">
                {{ $t("modals.enrollDevices.download.title") }}
              </span>
              <v-chip variant="flat" size="x-small" color="indigo-sky" label rounded="xs">
                <v-icon icon="$star" size="14" />
                <span class="ml-1 text-primary text-uppercase coro-label-sm">
                  {{ $t("modals.enrollDevices.recommended") }}
                </span>
              </v-chip>
            </div>
            <div class="body2 text-indigo-medium">
              {{ $t("modals.enrollDevices.download.description") }}
            </div>
          </div>
        </div>

        <v-menu>
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="flat"
              color="primary"
              size="44"
              class="ml-4 action-btn"
              rounded
            >
              <v-icon icon="$export" color="white" size="24" />
            </v-btn>
          </template>
          <v-list class="enroll-devices-os-list" density="compact">
            <v-list-item
              v-for="item in endpointVersions"
              :key="item.id"
              :data-testid="`download-os-${item.osType}`"
              @click="onDownloadClick(item)"
            >
              <template #prepend>
                <v-icon :icon="osTypeToIconMap[item.osType]" size="24" />
              </template>
              <v-list-item-title>{{ osLabel(item.osType) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Card 2: Share link -->
      <div class="enroll-card rounded-lg pa-6 d-flex align-center justify-space-between">
        <div class="d-flex align-start ga-3">
          <v-icon icon="icon-link-xs" size="24" class="mt-1" />
          <div>
            <span class="subtitle2">
              {{ $t("modals.enrollDevices.copyLink.title") }}
            </span>
            <div class="body2 text-indigo-medium mt-1">
              {{ $t("modals.enrollDevices.copyLink.description") }}
            </div>
          </div>
        </div>

        <v-menu>
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="flat"
              color="primary"
              size="44"
              class="ml-4 action-btn"
              rounded
            >
              <v-icon icon="icon-link-xs" color="white" size="24" />
            </v-btn>
          </template>
          <v-list class="enroll-devices-os-list" density="compact">
            <v-list-item
              v-for="item in endpointVersions"
              :key="item.id"
              :data-testid="`copy-link-os-${item.osType}`"
              @click="onCopyLinkClick(item)"
            >
              <template #prepend>
                <v-icon :icon="osTypeToIconMap[item.osType]" size="24" />
              </template>
              <v-list-item-title>{{ osLabel(item.osType) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div class="mt-6 d-flex flex-column ga-2">
      <span class="subtitle2">
        {{ $t("modals.enrollDevices.deployingWithRmm") }}
      </span>
      <div class="d-flex align-center" @click="onLearnAboutMDClick" role="button">
        <div class="mr-1 body2 coro-link">
          {{ $t("modals.enrollDevices.learnAboutMassDeployment") }}
        </div>
        <v-icon icon="$externalLink" size="14" />
      </div>
    </div>

    <div class="d-flex justify-end mt-6">
      <v-btn color="primary" rounded size="large" variant="flat" @click="$emit('dismissed')">
        {{ $t("general.done") }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import {
  type DeviceVersionItem,
  useDevicesSettingsStore,
} from "@/_store/devices-settings.module.ts";
import { copyToClipboard } from "@coronet/cui";
import { getLatestVersionsByOsType } from "@/_helpers/devices";
import { createDownloadLink, downloadFileByLink } from "@/_helpers/download";
import { OsType } from "@/constants/devices.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { ProtectionLimit } from "@/constants/devices.ts";
import ProtectionLimitBanner from "@/components/ProtectionLimitBanner.vue";

const osTypeToIconMap: Partial<Record<OsType, string>> = {
  [OsType.WINDOWS]: "icon-windows-logo-s",
  [OsType.LINUX]: "icon-linux-xs",
  [OsType.OSX]: "icon-apple-xs",
};

const osLabelMap: Partial<Record<OsType, string>> = {
  [OsType.WINDOWS]: "modals.enrollDevices.windowsAgent",
  [OsType.OSX]: "modals.enrollDevices.macosAgent",
  [OsType.LINUX]: "modals.enrollDevices.linuxAgent",
};

export default defineComponent({
  components: {
    ProtectionLimitBanner,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  emits: ["dismissed"],
  setup(props) {
    const i18n = useI18n();
    const devicesSettingsStore = useDevicesSettingsStore();
    const { availableVersions, downloadClientPageLink } = storeToRefs(devicesSettingsStore);

    const protectionLimit = computed<ProtectionLimit | null>(() => {
      return props.config.item?.protectionLimit ?? null;
    });

    const endpointVersions = computed(() => {
      return getLatestVersionsByOsType(availableVersions.value);
    });

    const osLabel = (osType: OsType): string => {
      return i18n.t(osLabelMap[osType] ?? osType);
    };

    const snackbarStore = useSnackbarStore();

    const onDownloadClick = async (item: DeviceVersionItem) => {
      const downloadLink = await createDownloadLink(item, downloadClientPageLink.value);
      downloadFileByLink(downloadLink);
    };

    const onCopyLinkClick = async (item: DeviceVersionItem) => {
      const downloadLink = await createDownloadLink(item, downloadClientPageLink.value);
      copyToClipboard(downloadLink);
      snackbarStore.addGenericSuccess(i18n.t("snackbar.messages.general.copiedToClipboard"));
    };

    const onLearnAboutMDClick = () => {
      window.open("https://docs.coro.net/devices/agent-deploy/", "_blank");
    };

    onMounted(async () => {
      await devicesSettingsStore.getAvailableVersions(false);
      await devicesSettingsStore.getDownloadClientPageLink();
    });

    return {
      protectionLimit,
      endpointVersions,
      osTypeToIconMap,
      osLabel,
      onDownloadClick,
      onCopyLinkClick,
      onLearnAboutMDClick,
    };
  },
});
</script>

<style lang="scss" scoped>
.enroll-card {
  border: 1px solid rgb(var(--v-theme-indigo-pale));
}

.icon-external-link-xs::before {
  color: rgb(var(--v-theme-link)) !important;
}

.action-btn :deep(.v-icon::before) {
  color: rgb(var(--v-theme-white)) !important;
}

.enroll-devices-os-list {
  :deep(.v-list-item__prepend) {
    opacity: 1 !important;

    .v-icon {
      opacity: 1 !important;

      &::before {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }
  }

  :deep(.v-list-item-title) {
    color: rgb(var(--v-theme-primary)) !important;
  }
}
</style>
